/**
 * ЧЕЛОВЕК ВВОДИТ РУБЛИ, КОНТРАКТ ПРИНИМАЕТ КОПЕЙКИ — между ними этот файл.
 *
 * ⚠️ ОШИБКА ЗДЕСЬ ТИХАЯ И РОВНО В СТО РАЗ: обе величины `number`, поэтому ни `tsc`, ни линт её не
 * увидят. Отправишь рубли — бронь на 37 200 ₽ уедет как 372 ₽, менеджер увидит «372 ₽», а гость
 * заплатит по счёту сервера. Поэтому умножение живёт в ОДНОМ месте (`shared/lib/format`: суммы
 * вводятся ещё и в редакторе суммы шага), у него есть имя и тест. Здесь остаётся РАЗБОР ввода этой
 * формы. Именование тоже не косметика: `totalRubles` и `total` взглядом не перепутать, а `total` и
 * `total` — можно.
 *
 * ⚠️ ПОЛЕЙ `prepay` И `deposit` В КОНТРАКТЕ НЕТ: у сущности брони им места нет, а принятые при
 * заведении деньги едут позициями `initialPayments`.
 */

import { pluralizeRu, rublesToKopecks } from '@/shared/lib/format'

/** Прочерк вместо суммы: макет ставит его, пока сумма не введена (`nbPerNight`, `nbRest`). */
export const DASH = '—'

/**
 * Целые рубли из напечатанного. Разбор как в макете (`prototype/Prototype.dc.html:6021`): выкинуть
 * пробелы (разделитель разрядов человеку никто не запрещал), взять целое, мусор → 0. Отрицательное
 * тоже → 0: минус в сумме брони не значит ничего, а `-5000` копеек сервер принял бы молча.
 */
export function parseRubles(input: string): number {
  const value = Number.parseInt(input.replace(/\s/g, ''), 10)
  return Number.isFinite(value) && value > 0 ? value : 0
}

/** Рубли → копейки. Перевод общий на приложение — `shared/lib/format`. */
export function toKopecks(rubles: number): number {
  return rublesToKopecks(rubles)
}

/**
 * Ночей между заездом и выездом.
 *
 * ⚠️ `Date.parse('2026-07-03')` — это UTC-полночь (так велит спецификация для date-only формы),
 * поэтому разность двух таких дат кратна суткам и часовой пояс машины её не сдвигает. `new Date(y,
 * m, d)` (локальная полночь) дал бы 2.958333 ночи на переходе летнего времени, а `Math.round` прятал
 * бы это до тех пор, пока не спрятать.
 *
 * Ноль — это и «даты не введены», и «выезд раньше заезда»: для формы оба случая одинаковы.
 */
export function nightsBetween(checkIn: string, checkOut: string): number {
  const from = Date.parse(checkIn)
  const to = Date.parse(checkOut)
  if (Number.isNaN(from) || Number.isNaN(to)) return 0
  return Math.max(0, Math.round((to - from) / 86_400_000))
}

/**
 * Бейдж ночей. Склонение — общей `pluralizeRu`, той же, что печатает «3 ночи» в шапке карточки
 * брони: своя копия правила разъехалась бы, а разъезжается всегда та, о которой забыли.
 *
 * ⚠️ ОСОЗНАННОЕ ОТСТУПЛЕНИЕ ОТ МАКЕТА, И ОНО РОВНО В СКЛОНЕНИИ. Макет считает форму наивно
 * (`prototype/Prototype.dc.html:7179`, `nights >= 2 && nights <= 4 ? 'ночи' : 'ночей'`) и на 21 ночи
 * печатает «21 ночей». Помесячная аренда не экзотика, а бейдж висит прямо над полями дат.
 * `pluralizeRu` знает правило целиком: mod 10 / mod 100 с исключением 11–14.
 */
export function nightsLabel(nights: number): string {
  return `${nights} ${pluralizeRu(nights, ['ночь', 'ночи', 'ночей'])}`
}

/** «За ночь» в сводке (`prototype/Prototype.dc.html:6022`). Показываем РУБЛИ — копейки появятся только в теле запроса. */
export function perNightLabel(totalRubles: number, nights: number): string {
  if (nights <= 0 || totalRubles <= 0) return DASH
  return Math.round(totalRubles / nights).toLocaleString('ru-RU')
}

/*
 * ⚠️ ОСТАТКА К ОПЛАТЕ ЗДЕСЬ НЕТ, И ЭТО РЕШЕНИЕ, А НЕ ПРОБЕЛ: считает его СЕРВЕР в своде секции
 * платежей — у него все записи брони, а не одна строка формы, и после первой же ручной записи наш
 * счёт разошёлся бы с его. Предоплата у формы при этом есть, так что соблазн «раз есть предоплата,
 * посчитаем и остаток» будет.
 *
 * ⚠️ ЗАЛОГ В ОПЛАЧЕННОЕ НЕ ВХОДИТ: он возвращается гостю при выезде — обеспечение, а не оплата
 * проживания. Во фронте это записано только здесь.
 */
